import { useCallback, useMemo, useState } from "react";
import {
    TransactionView,
    TransactionViewSchema,
} from "@common/types/views/transactionViews";
import { View } from "@common/types/views/views";
import { createRuleTransactionsPreviewView } from "./ruleTransactionsPreviewView";

type PreviewRuleKind = "split" | "accounting" | "standard";

function getPreviewRuleKind({
    isSplitRule,
    isAccountingRule,
}: {
    isSplitRule: boolean;
    isAccountingRule: boolean;
}): PreviewRuleKind {
    if (isSplitRule) {
        return "split";
    }
    if (isAccountingRule) {
        return "accounting";
    }
    return "standard";
}

export function useRuleTransactionsPreviewView({
    isSplitRule,
    isAccountingRule,
    isShowingApplied,
}: {
    isSplitRule: boolean;
    isAccountingRule: boolean;
    isShowingApplied: boolean;
}) {
    const ruleKind = getPreviewRuleKind({ isSplitRule, isAccountingRule });
    const defaultView = useMemo(
        () =>
            createRuleTransactionsPreviewView({
                isSplitRule,
                isAccountingRule,
                isShowingApplied,
            }),
        [isSplitRule, isAccountingRule, isShowingApplied],
    );

    const [customization, setCustomization] = useState<{
        ruleKind: PreviewRuleKind;
        view: TransactionView;
    }>();

    const view = useMemo(() => {
        const source =
            customization?.ruleKind === ruleKind
                ? customization.view
                : defaultView;

        return {
            ...source,
            options: {
                ...source.options,
                bulkActions: !isShowingApplied,
                selection: !isShowingApplied,
                readonly: true,
            },
        };
    }, [customization, defaultView, isShowingApplied, ruleKind]);

    const handleViewChange = useCallback(
        (updatedView: View) => {
            const parsed = TransactionViewSchema.safeParse(updatedView);
            if (!parsed.success) {
                return;
            }
            setCustomization({ ruleKind, view: parsed.data });
        },
        [ruleKind],
    );

    return { view, defaultView, handleViewChange };
}
